import { defineStore } from "pinia";
import { ref, computed, reactive, watch, toRaw } from "vue";
import { useOrganizations } from "./organizations";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { DataInstance, Organizations } from "@/platform-data";

declare global {
    interface Window { GoogleMapsApiInit: () => void; }
}

export const useGoogleMaps = defineStore('google-maps', () => {
    const organizations = useOrganizations();
    const initialized = ref(false);
    const mapsInitialized = computed(() => initialized.value);
    const waitingPromises = ref<(() => void)[]>([]);
    const selectedOrganization = ref<string|null>(null);
    const selectedOrgDetails =ref<DataInstance<Organizations.Model> | null>(null);
    const markers = ref<google.maps.marker.AdvancedMarkerElement[]>([]);
    const markerClusterController = ref<MarkerClusterer | null>(null);


    const Lib = reactive({
        Maps: null as google.maps.MapsLibrary | null,
        Marker: null as google.maps.MarkerLibrary | null,
        Geocoding: null as google.maps.GeocodingLibrary | null,
        Core: null as google.maps.CoreLibrary | null,
        Places: null as google.maps.PlacesLibrary | null
    });

    function createMapScript() {
        const scriptElement = document.createElement('script');
        const scriptUrl = new URL('https://maps.googleapis.com/maps/api/js');
        scriptUrl.searchParams.append('callback', "GoogleMapsApiInit");
        scriptUrl.searchParams.append('key', CONFIG.google.maps.apiKey);
        scriptUrl.searchParams.append("loading", "async");

        scriptElement.src = scriptUrl.toString();
        scriptElement.async = true;
        scriptElement.defer = true;
        document.head.appendChild(scriptElement);
    };

    async function mapsInit() {
        try {
            Lib.Maps = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
            Lib.Marker = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
            Lib.Core = await google.maps.importLibrary("core") as google.maps.CoreLibrary;
            Lib.Places = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

            console.log("Google Maps libraries loaded");
            initialized.value = true;
            waitingPromises.value.forEach(res => res());
        } catch (error) {
            console.error("Error loading Google Maps libraries:", error);
        }
    }

    function waitForReady() {
        if (!initialized.value) return new Promise<void>(res => { waitingPromises.value.push(res) });
        else return Promise.resolve();
    }

    const mapInstance = ref<google.maps.Map | null>(null);


    function useMap(el: HTMLElement) {
        const map = new google.maps.Map(el, {
            zoom: 2,
            center: { lat: 13.922043, lng: -37.859651 },
            streetViewControl: false,
            zoomControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            mapId: CONFIG.google.maps.mapId
        });

        mapInstance.value = map;
        addMarkers(map);
        return { map };
    }

    function setTitleInInfoWindow(title:string) {
        // Query for the gm-style-iw-chr div
        const iwChr = document.querySelector('.gm-style-iw-chr');
        
        if (iwChr) {
            // Create a title element
            const titleElement = document.createElement('div');
            titleElement.className = 'title'
            titleElement.innerText = title;
            titleElement.style.fontWeight = 'bold';
            titleElement.style.fontSize = '16px';
    
            // Insert the title before the close button
            iwChr.insertBefore(titleElement, iwChr.firstChild);
        }
    }

    function selectOrganization(orgName: string | null) {
        selectedOrganization.value = orgName;
    }

    function openOrganizationDetails(org: DataInstance<Organizations.Model>){
        selectedOrgDetails.value = org
    };

    function populateInfoWindow(org: Organizations.Model){
        let contentString = `<div id="content">`;
        if(org['HQ Street Address']){
            const tmp = `<p><strong>HQ Street Address:</strong> ${org['HQ Street Address']}</p>`
            contentString += tmp;
        }

        if(org['Office #']){
            const tmp = `<p><strong>Office #:</strong> ${org['Office #']}</p>`
            contentString += tmp;
        }

        if(org['Category']){
            const tmp = `<p><strong>Category:</strong> ${org['Category']}</p>`
            contentString += tmp;
        }

        if(org["NON PROFIT"] !== null ){
             const tmp = `<p><strong>Advocacy:</strong> ${org['NON PROFIT']}</p>`
        }
        const buttonId = `${org['Organization Name']}-btn`;
        const detailsId = `${org['Organization Name']}-details`
        const button = `<button id="${buttonId}" class="open-db-btn">Open in Database</button>`;
        const detailButton = `<button id="${detailsId}" class="detail-btn">View More Details</button>`;

        let buttonWrapper = `<div class="btn-wrapper">`;
        buttonWrapper += button;
        buttonWrapper += detailButton;
        buttonWrapper += '</div>';
        //contentString += button;
        contentString += buttonWrapper;
        contentString += '</div>';
        return contentString;
    }

    let currentInfoWindow: google.maps.InfoWindow | null = null;

    function openInfoWindow(marker: google.maps.marker.AdvancedMarkerElement, org: Organizations.Model, map: google.maps.Map) {
        if (currentInfoWindow) {
            currentInfoWindow.close();
        }

        const infoWindow = new google.maps.InfoWindow({
            content: populateInfoWindow(org),
        });

        infoWindow.open(map, marker);
        currentInfoWindow = infoWindow;

        google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
            setTitleInInfoWindow(org['Organization Name']);
            setupInfoWindowButtons(org);
        });

        infoWindow.addListener('closeclick', () => {
            currentInfoWindow = null;
        });
    }

    async function addMarkers(map: google.maps.Map) {
        const orgs = Object.values(organizations.organizations);
    
        // Prepare all markers asynchronously
        const markerPromises: Promise<google.maps.marker.AdvancedMarkerElement | null>[] = [];
    
         // Create a single infoWindow to reuse
        const infoWindow = new google.maps.InfoWindow();
        let currentInfoWindow: google.maps.InfoWindow | null = null;
        let hoveredMarker: google.maps.marker.AdvancedMarkerElement | null = null;
        let hoveredDetail: boolean = false;
        let closeTimeout: number | null = null;

        for (const org of orgs) {
            if (org.lat && org.lng) {
                // Create an img element for the custom SVG marker
                const svgElement = document.createElement('img');
                //svgElement.src = '../resources/marker.svg'; 
                svgElement.src = "https://storage.googleapis.com/als-public/web-widget/resources/marker.svg";
                svgElement.style.width = '40px'; 
                svgElement.style.height = '40px'; 

                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat: org.lat, lng: org.lng },
                    map: map,
                    content: svgElement,
                });

                const openInfoWindow = () => {

                    let currentInfoWindow: google.maps.InfoWindow | null = null;

                    if (currentInfoWindow) {
                        currentInfoWindow.close();
                    }

                    const contentString = populateInfoWindow(org);
                    infoWindow.setContent(contentString);
                    infoWindow.open(map, marker);

                    currentInfoWindow = infoWindow;
    
                    google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
                        setTitleInInfoWindow(org['Organization Name']);
    
                        const buttonId = `${org['Organization Name']}-btn`;
                        const detailsId = `${org['Organization Name']}-details`;
                        const openInDatabaseBtn = document.getElementById(buttonId);
                        const detailsBtn = document.getElementById(detailsId);
    
                        if (openInDatabaseBtn) {
                            openInDatabaseBtn.addEventListener('click', () => {
                                selectOrganization(org['Organization Name']);
                            });
                        }
    
                        if (detailsBtn) {
                            detailsBtn.addEventListener('click', () => {
                                openOrganizationDetails(org);
                            });
                        }

                        if (detailsBtn) {
                            detailsBtn.addEventListener('mouseenter', () => {
                                hoveredDetail = true; // User is hovering over the detail view
                            });
    
                            detailsBtn.addEventListener('mouseleave', () => {
                                hoveredDetail = false; // User is no longer hovering over the detail view

                                // if (!hoveredMarker) {
                                //     closeTimeout = window.setTimeout(() => {
                                //         if (!hoveredMarker && !hoveredDetail) {
                                //             currentInfoWindow?.close();
                                //             currentInfoWindow = null;
                                //         }
                                //     }, 200);
                                // }
                            });
                        }
                    });
                };

                marker.addListener('click', () => {
                    openInfoWindow(); // Open the info window on click
                });

                marker.element.addEventListener("mouseover", () => {
                    marker.element.classList.add("marker-container:hover");
                    hoveredMarker = marker;
                    if (closeTimeout) {
                        window.clearTimeout(closeTimeout);
                    }
                    openInfoWindow();
                });
    
                // Remove hover info window and reset marker size
                marker.element.addEventListener("mouseout", () => {
                    marker.element.classList.remove("marker-container:hover");

                    hoveredMarker = null; // User is no longer hovering over this marker

                    // if (!hoveredDetail) {
                    //     closeTimeout = window.setTimeout(() => {
                    //         if (!hoveredMarker && !hoveredDetail) {
                    //             currentInfoWindow?.close();
                    //             currentInfoWindow = null;
                    //         }
                    //     }, 200);
                    // }
                });
                

                
                markerPromises.push(
                    Promise.resolve(marker)
                );
            }
        }
    
        // Wait for all marker promises to resolve and filter out null values
        const resolvedMarkers = await Promise.all(markerPromises);
        markers.value = resolvedMarkers.filter((marker): marker is google.maps.marker.AdvancedMarkerElement => marker !== null);
    
        if (markers.value.length > 0) {
            // Add a marker clusterer to manage the markers.
            markerClusterController.value = new MarkerClusterer({
                map: map,
                markers: toRaw(markers.value),
                renderer: {
                    render({ count, position }) {
                        const clusterElement = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                        clusterElement.setAttribute("width", "50");
                        clusterElement.setAttribute("height", "50");
                        clusterElement.setAttribute("viewBox", "0 0 50 50");
    
                        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
                        circle.setAttribute("cx", "25");
                        circle.setAttribute("cy", "25");
                        circle.setAttribute("r", "22");
                        circle.setAttribute("stroke", "rgba(255,255,255, 0.82");
                        circle.setAttribute("stroke-width", "2");
                        circle.setAttribute("fill", "rgba(26, 97, 78, 0.82)");
    
                        clusterElement.appendChild(circle);
    
                        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
                        text.setAttribute("x", "25");
                        text.setAttribute("y", "30");
                        text.setAttribute("fill", "rgba(255,255,255, 0.82");
                        text.setAttribute("text-anchor", "middle");
                        text.setAttribute("font-size", "14");
                        text.setAttribute("font-weight", "bold");
                        text.textContent = String(count);
    
                        clusterElement.appendChild(text);
    
                        return new google.maps.marker.AdvancedMarkerElement({
                            position,
                            map: map,
                            content: clusterElement,
                        });
                    },
                },
            });

            // Set the map property on all markers to make them visible
            markers.value.forEach(marker => marker.map = map);
            console.log("Markers added and clusterer initialized");
        } else {
            console.log("No markers to add");
        }
    }

    function clearMarkers() {
        while(markers.value.length) {
            let marker = markers.value.pop();
            if(marker) {
                marker.map = null;
            }
        }

        markers.value.forEach(marker => marker.map = null);
        markers.value = [];

        if (markerClusterController.value) {
            markerClusterController.value.clearMarkers();
        }
    
        console.log("All markers cleared.");
    }

    const updateClusters = () => {
        if(markerClusterController.value) {
            markerClusterController.value.addMarkers(markers.value);
            console.log("Updated clusters with markers");
        }
    }

    function frameMarkers() {
        setTimeout(() => {
            if(!mapInstance.value) {
                console.error("Map instance is not initialized.");
                return;
            }
            const bounds = new google.maps.LatLngBounds();
            for(const m of markers.value) {
                if(m.position) bounds.extend(m.position);
            }
            mapInstance.value.fitBounds(bounds);
            google.maps.event.trigger(mapInstance.value,'resize')
        },10);
    
    }

    const updateMapMarkers = (filteredOrganizations: any[]) => {
        if (!mapInstance.value) {
            console.error("Map instance is not initialized.");
            return;
        }
        
        // Clear existing markers
        clearMarkers();

        const infoWindow = new google.maps.InfoWindow();
        let currentInfoWindow: google.maps.InfoWindow | null = null;
        
        filteredOrganizations.forEach(org => {
            if (typeof org.lat !== "number" || typeof org.lng !== "number" || isNaN(org.lat) || isNaN(org.lng)) {
                console.error(`Invalid coordinates for ${org["Organization Name"]}:`, org.lat, org.lng);
                return;
            }
    
            const svgElement = document.createElement('img');
            svgElement.src = "https://storage.googleapis.com/als-public/web-widget/resources/marker.svg";
            svgElement.style.width = '40px';
            svgElement.style.height = '40px';
    
            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: { lat: org.lat, lng: org.lng },
                map: mapInstance.value,
                title: org["Organization Name"],
                content: svgElement
            });

            const openInfoWindow = () => {
                if (currentInfoWindow) {
                    currentInfoWindow.close();
                }
    
                const contentString = populateInfoWindow(org);
                infoWindow.setContent(contentString);
                infoWindow.open(mapInstance.value, marker);
                currentInfoWindow = infoWindow;
    
                google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
                    setTitleInInfoWindow(org['Organization Name']);
    
                    const buttonId = `${org['Organization Name']}-btn`;
                    const detailsId = `${org['Organization Name']}-details`;
                    const openInDatabaseBtn = document.getElementById(buttonId);
                    const detailsBtn = document.getElementById(detailsId);
    
                    if (openInDatabaseBtn) {
                        openInDatabaseBtn.addEventListener('click', () => {
                            selectOrganization(org['Organization Name']);
                        });
                    }
    
                    if (detailsBtn) {
                        detailsBtn.addEventListener('click', () => {
                            openOrganizationDetails(org);
                        });
                    }
                });
            };
    
            marker.addListener('click', () => {
                openInfoWindow();
            });
    
            marker.element.addEventListener("mouseover", () => {
                marker.element.classList.add("marker-container:hover");
                openInfoWindow();
            });
    
            marker.element.addEventListener("mouseout", () => {
                marker.element.classList.remove("marker-container:hover");
            });
    
            markers.value.push(marker);
        });

        markers.value.forEach(marker => marker.map = mapInstance.value);

        if (markerClusterController.value) {
            markerClusterController.value.clearMarkers(); // Clear existing markers
            markerClusterController.value.addMarkers(markers.value); // Re-add markers
        }
    
        // Force the map to repaint
        google.maps.event.trigger(mapInstance.value, 'resize');
    
        // // Adjust zoom slightly to trigger a refresh
        const currentZoom = mapInstance.value.getZoom();
        if(currentZoom) {
            mapInstance.value.setZoom(currentZoom + 1);
            setTimeout(() => mapInstance.value?.setZoom(currentZoom), 100);
        }

        updateClusters();
        frameMarkers();
    }
    
	function setOrgDetails(org:DataInstance<Organizations.Model>){
        selectedOrgDetails.value = org;
    }

    window.GoogleMapsApiInit = mapsInit;
    createMapScript();

    return { waitForReady, markers, useMap, updateClusters, mapsInitialized, Lib, selectedOrganization, selectOrganization, selectedOrgDetails, setOrgDetails, addMarkers, mapInstance, clearMarkers, populateInfoWindow, updateMapMarkers };
});